import { z } from "zod";

/**
 * Typed, validated environment. Nothing else in the app may read
 * `import.meta.env` — the rule is the same one the services follow with
 * ConfigService: one validated object, and a loud failure at startup rather
 * than `undefined` showing up in a URL three screens later.
 */
const envSchema = z.object({
  /**
   * Base URL of bella-admin-api INCLUDING its `/v1` prefix, no trailing slash.
   * e.g. http://localhost:3101/v1
   *
   * Either bella entrypoint works — both serve identical `/v1/auth/*` paths,
   * call the same AuthService, and carry the CORS + cookie fixes the browser
   * needs. Production serves the Express one (`dist/server.js`).
   *
   * Do NOT point this at `npm run dev:nest`: tsx drops decorator metadata, so
   * Nest injects undefined dependencies and every successful response 500s.
   */
  VITE_API_BASE_URL: z
    .string()
    .url("VITE_API_BASE_URL must be an absolute URL, e.g. http://localhost:3101/v1")
    .transform((url) => url.replace(/\/+$/, "")),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  const detail = parsed.error.issues
    .map((issue) => `  ${issue.path.join(".")}: ${issue.message}`)
    .join("\n");
  // Thrown, not logged: a misconfigured API URL cannot produce a working app,
  // and a blank screen with a clear console error beats a subtly broken one.
  throw new Error(
    `Invalid environment configuration.\n${detail}\n\nCopy .env.example to .env and fill it in.`,
  );
}

export const env = parsed.data;
